/* ═══════════════════════════════════════════════════════════════════════════
   RALJE — MIDNIGHT ATELIER · Motion Layer
   Volume III · MMXXVI
   ---------------------------------------------------------------------------
   Everything degrades gracefully. If IntersectionObserver is missing, content
   simply appears. If the visitor has asked for reduced motion, we honour it
   and skip every non-essential effect.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var REDUCED = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* Throttle to one call per animation frame */
  function onFrame(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        fn();
      });
    };
  }


  /* ─────────────────────────────────────────────────────────────────────────
     01 · THE VEIL — lift the loader once the page is ready
     ───────────────────────────────────────────────────────────────────────── */

  function liftVeil() {
    var veil = document.querySelector('.ralje-veil');
    if (!veil) return;

    var lift = function () {
      veil.classList.add('is-lifted');
      window.setTimeout(function () {
        if (veil.parentNode) veil.parentNode.removeChild(veil);
      }, 1000);
    };

    if (REDUCED) { lift(); return; }
    window.setTimeout(lift, 900);
  }

  /* Never let the veil trap anyone, whatever else fails */
  window.setTimeout(function () {
    var veil = document.querySelector('.ralje-veil');
    if (veil) veil.classList.add('is-lifted');
  }, 3500);


  /* ─────────────────────────────────────────────────────────────────────────
     02 · NAVIGATION — frosted on scroll, drawer on mobile
     ───────────────────────────────────────────────────────────────────────── */

  function initNav() {
    var nav = document.querySelector('.atelier-nav');
    if (nav) {
      var setNavState = onFrame(function () {
        nav.classList.toggle('scrolled', window.scrollY > 60);
      });
      window.addEventListener('scroll', setNavState, { passive: true });
      setNavState();
    }

    var toggle = document.querySelector('.atelier-nav__toggle');
    var links  = document.querySelector('.atelier-nav__links');
    if (!toggle || !links) return;

    var setOpen = function (open) {
      links.classList.toggle('is-open', open);
      toggle.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
    };

    toggle.addEventListener('click', function () {
      setOpen(!links.classList.contains('is-open'));
    });

    /* Close the drawer whenever a destination is chosen */
    links.addEventListener('click', function (e) {
      if (e.target.closest('a')) setOpen(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && links.classList.contains('is-open')) setOpen(false);
    });
  }


  /* ─────────────────────────────────────────────────────────────────────────
     03 · REVEALS — fade sections in as they enter the frame
     ───────────────────────────────────────────────────────────────────────── */

  function initReveals() {
    var targets = document.querySelectorAll('.fade-in, .timeline__item');

    if (REDUCED || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (el) {
        el.classList.add('visible');
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    Array.prototype.forEach.call(targets, function (el) { observer.observe(el); });
  }


  /* ─────────────────────────────────────────────────────────────────────────
     04 · GOLD SWEEP — light travels across the headline, once, on entry
     ───────────────────────────────────────────────────────────────────────── */

  function initSweep() {
    var marks = document.querySelectorAll('.gold-sweep');
    if (!marks.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        window.setTimeout(function () { el.classList.add('is-lit'); }, 320);
        observer.unobserve(el);
      });
    }, { threshold: 0.5 });

    Array.prototype.forEach.call(marks, function (el) { observer.observe(el); });
  }


  /* ─────────────────────────────────────────────────────────────────────────
     05 · THE SPINE — the heritage rule lights as you descend it
     ───────────────────────────────────────────────────────────────────────── */

  function initSpine() {
    var timeline = document.querySelector('.timeline');
    var spine    = timeline && timeline.querySelector('.timeline__line');
    if (!timeline || !spine || REDUCED) return;

    var update = onFrame(function () {
      var box     = timeline.getBoundingClientRect();
      var viewMid = window.innerHeight * 0.62;
      var travel  = (viewMid - box.top) / box.height;
      var pct     = Math.max(0, Math.min(1, travel)) * 100;
      spine.style.setProperty('--spine', pct.toFixed(2) + '%');
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }


  /* ─────────────────────────────────────────────────────────────────────────
     06 · PARALLAX — heritage portraits drift a little slower than the page
     ───────────────────────────────────────────────────────────────────────── */

  function initParallax() {
    if (REDUCED) return;
    if (window.matchMedia && window.matchMedia('(max-width: 860px)').matches) return;

    var media = document.querySelectorAll('.timeline__img-wrap, .hero__watermark');
    if (!media.length) return;

    var update = onFrame(function () {
      var mid = window.innerHeight / 2;
      Array.prototype.forEach.call(media, function (el) {
        var box = el.getBoundingClientRect();
        if (box.bottom < -200 || box.top > window.innerHeight + 200) return;
        var offset = ((box.top + box.height / 2) - mid) / mid;
        var depth  = el.classList.contains('hero__watermark') ? 26 : 14;
        el.style.setProperty('--drift', (-offset * depth).toFixed(2) + 'px');
      });
    });

    Array.prototype.forEach.call(media, function (el) {
      if (el.classList.contains('timeline__img-wrap')) {
        el.style.willChange = 'transform';
        el.style.transform  = 'translate3d(0, var(--drift, 0px), 0)';
      }
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }


  /* ─────────────────────────────────────────────────────────────────────────
     07 · GALLERY OF LEGACIES
          Open a story · filter by piece type · reveal the rest of the shelf

          The rail and the cards are rendered by Liquid, so the categories
          come straight from what is typed in the Theme Customizer. This
          only decides what is on show.
     ───────────────────────────────────────────────────────────────────────── */

  function initLegacies() {
    var section = document.querySelector('.legacies');
    var grid    = section && section.querySelector('.legacy-grid');
    if (!grid) return;

    var cards   = Array.prototype.slice.call(grid.querySelectorAll('.legacy-card'));
    var filters = Array.prototype.slice.call(section.querySelectorAll('.legacy-filter'));
    var moreBtn = section.querySelector('.legacy-more__btn');
    var initial = parseInt(grid.getAttribute('data-initial'), 10) || 6;

    var kind = '*';
    var expanded = false;

    /* ── What should be on show right now ── */
    function paint() {
      var shown = 0;

      cards.forEach(function (card) {
        var matches = (kind === '*') || (card.getAttribute('data-kind') === kind);
        var show;

        if (!matches) {
          show = false;
        } else if (kind !== '*') {
          /* A chosen category always shows everything it holds */
          show = true;
        } else {
          show = expanded || shown < initial;
        }

        if (show) shown++;
        card.classList.toggle('is-hidden', !show);

        if (!show && card.classList.contains('active')) {
          card.classList.remove('active');
          card.setAttribute('aria-expanded', 'false');
        }
      });

      if (!moreBtn) return;

      /* The control only belongs to the unfiltered view */
      var hasOverflow = cards.length > initial;
      moreBtn.parentNode.style.display = (kind === '*' && hasOverflow) ? '' : 'none';
      moreBtn.setAttribute('aria-expanded', String(expanded));

      var label = moreBtn.querySelector('.legacy-more__label');
      if (label) {
        label.textContent = expanded
          ? moreBtn.getAttribute('data-less')
          : moreBtn.getAttribute('data-more');
      }

      var count = moreBtn.querySelector('.legacy-more__count');
      if (count) count.style.display = expanded ? 'none' : '';
    }

    /* ── Open one story at a time ── */
    cards.forEach(function (card) {
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'button');
      card.setAttribute('aria-expanded', 'false');

      var toggle = function () {
        var wasActive = card.classList.contains('active');
        cards.forEach(function (c) {
          c.classList.remove('active');
          c.setAttribute('aria-expanded', 'false');
        });
        if (!wasActive) {
          card.classList.add('active');
          card.setAttribute('aria-expanded', 'true');
        }
      };

      card.addEventListener('click', function (e) {
        /* A Ledger link inside the story is a destination, not a toggle */
        if (e.target.closest('a')) return;
        toggle();
      });

      card.addEventListener('keydown', function (e) {
        if (e.target.closest('a')) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
    });

    /* ── The category rail ── */
    filters.forEach(function (btn) {
      btn.addEventListener('click', function () {
        kind = btn.getAttribute('data-kind') || '*';
        expanded = false;

        filters.forEach(function (b) {
          var on = (b === btn);
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', String(on));
        });

        paint();
      });
    });

    /* ── View all / show fewer ── */
    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        expanded = !expanded;
        paint();

        if (!expanded) {
          var top = grid.getBoundingClientRect().top + window.pageYOffset - 120;
          window.scrollTo({ top: top, behavior: REDUCED ? 'auto' : 'smooth' });
        }
      });
    }

    paint();
  }


  /* ─────────────────────────────────────────────────────────────────────────
     08 · MAGNETIC CONTROLS — buttons lean toward the cursor
     ───────────────────────────────────────────────────────────────────────── */

  function initMagnetic() {
    if (REDUCED) return;
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var controls = document.querySelectorAll('.hero__cta, .atelier-nav__cta, .form-submit');

    Array.prototype.forEach.call(controls, function (el) {
      el.addEventListener('mousemove', function (e) {
        var box = el.getBoundingClientRect();
        var dx  = (e.clientX - (box.left + box.width  / 2)) / box.width;
        var dy  = (e.clientY - (box.top  + box.height / 2)) / box.height;
        el.style.transform = 'translate(' + (dx * 9).toFixed(2) + 'px,' +
                                            (dy * 6).toFixed(2) + 'px)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.transform = '';
      });
    });
  }


  /* ─────────────────────────────────────────────────────────────────────────
     09 · SMOOTH ANCHORS — account for the fixed navigation
     ───────────────────────────────────────────────────────────────────────── */

  function initAnchors() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;

      var id = link.getAttribute('href');
      if (!id || id === '#' || id.length < 2) return;

      var target;
      try { target = document.querySelector(id); } catch (err) { return; }
      if (!target) return;

      e.preventDefault();

      var nav    = document.querySelector('.atelier-nav, .ralje-page-nav');
      var offset = nav ? nav.getBoundingClientRect().height : 0;
      var top    = target.getBoundingClientRect().top + window.pageYOffset - offset - 8;

      window.scrollTo({
        top: top,
        behavior: REDUCED ? 'auto' : 'smooth'
      });
    });
  }


  /* ─────────────────────────────────────────────────────────────────────────
     10 · CRAFT RAIL — duplicate the set so the loop is seamless
     ───────────────────────────────────────────────────────────────────────── */

  function initRail() {
    var track = document.querySelector('.craft-rail__track');
    if (!track) return;
    var set = track.querySelector('.craft-rail__set');
    if (!set || track.children.length > 1) return;
    var clone = set.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    track.appendChild(clone);
  }


  /* ── Ignition ─────────────────────────────────────────────────────────── */

  ready(function () {
    initNav();
    initReveals();
    initSweep();
    initSpine();
    initParallax();
    initLegacies();
    initMagnetic();
    initAnchors();
    initRail();
  });

  window.addEventListener('load', liftVeil);
})();
